/**
 * Trash — Iter 400.6.
 *
 * Tenant-wide Trash for owners/admins. Lists every soft-deleted row
 * across the app grouped by entity kind, with per-row Restore and
 * hard-purge actions plus a days-until-purge countdown so nothing
 * disappears without warning.
 *
 * Source of truth: GET /api/trash (returns { kinds, totals, buckets }).
 * Restore: POST /api/{kind}/{id}/restore.
 * Hard-purge: DELETE /api/{kind}/{id}/purge.
 */
import { useEffect, useMemo, useState } from "react";
import { Trash2, RefreshCw, Undo2, AlertTriangle, X } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";

const KIND_LABELS = {
  jobcards:        "Job cards",
  tickets:         "Support tickets",
  leads:           "Leads",
  customers:       "Customers",
  contacts:        "Contacts",
  equipment:       "Equipment",
  sales_documents: "Sales documents",
  calls:           "Call logs",
  datasheets:      "Datasheets",
  bulletins:       "Bulletins",
  picking_items:   "Picking items",
  picking_lists:   "Picking slips",
  gallery_items:   "Media / photos",
  gallery_folders: "Media folders",
  sites:           "Sites",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso.slice(0, 16).replace("T", " ");
  }
}

function labelForRow(row) {
  return row.name
    || row.title
    || row.subject
    || row.short_id
    || row.file_name
    || row.description
    || row.id?.slice(0, 8)
    || "(untitled)";
}

export default function Trash() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [activeKind, setActiveKind] = useState("all");

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data: d } = await api.get("/trash", {
        params: q ? { q } : {},
      });
      setData(d);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const buckets = data?.buckets || {};
  const bucketKeys = useMemo(() => Object.keys(buckets), [buckets]);
  const activeRows = useMemo(() => {
    if (activeKind === "all") {
      return Object.entries(buckets)
        .flatMap(([k, rows]) => rows.map((r) => ({ ...r, _kind: k })));
    }
    return (buckets[activeKind] || []).map((r) => ({ ...r, _kind: activeKind }));
  }, [buckets, activeKind]);

  const restore = async (row) => {
    try {
      await api.post(`/${row._kind}/${row.id}/restore`);
      toast.success(`${KIND_LABELS[row._kind] || row._kind} restored.`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const purge = async (row) => {
    const label = labelForRow(row);
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Permanently delete "${label}"?\n\nThis cannot be undone. The record and any attached files will be removed from the database.\n\nType Y to confirm.`,
    );
    if (!ok) return;
    try {
      await api.delete(`/${row._kind}/${row.id}/purge`);
      toast.success("Purged.");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="trash-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Trash2 className="w-6 h-6 text-rose-300" /> Trash
          </h1>
          <p className="text-sm text-[#a0aabf] mt-1 max-w-2xl">
            Everything deleted in the app stays here for 90 days before it&apos;s
            permanently purged. Click <strong>Restore</strong> to bring an item
            back exactly as it was.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            placeholder="Search deleted items…"
            className="hv-input text-sm"
            data-testid="trash-search"
          />
          <button
            type="button"
            onClick={load}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="trash-refresh"
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      {err && (
        <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="trash-error">
          {err}
        </div>
      )}

      {/* Kind filter chips */}
      <div className="flex flex-wrap gap-1" data-testid="trash-kind-filter">
        <button
          type="button"
          onClick={() => setActiveKind("all")}
          className={`px-3 py-1 rounded text-xs font-medium ${
            activeKind === "all" ? "bg-[#22d3ee]/20 text-[#22d3ee]" : "text-[#a0aabf] hover:text-white bg-white/[0.03]"
          }`}
          data-testid="trash-kind-all"
        >
          All ({data?.totals?.rows ?? 0})
        </button>
        {bucketKeys.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setActiveKind(k)}
            className={`px-3 py-1 rounded text-xs font-medium ${
              activeKind === k ? "bg-[#22d3ee]/20 text-[#22d3ee]" : "text-[#a0aabf] hover:text-white bg-white/[0.03]"
            }`}
            data-testid={`trash-kind-${k}`}
          >
            {KIND_LABELS[k] || k} ({buckets[k].length})
          </button>
        ))}
      </div>

      {/* Rows */}
      {loading && !data ? (
        <div className="hv-card p-6 text-center text-sm text-[#647087]">Loading…</div>
      ) : activeRows.length === 0 ? (
        <div className="hv-card p-8 text-center text-sm text-[#647087]" data-testid="trash-empty">
          Nothing in the Trash. 🎉
        </div>
      ) : (
        <div className="hv-card overflow-hidden">
          <ul className="divide-y divide-white/5">
            {activeRows.map((r) => (
              <li
                key={`${r._kind}-${r.id}`}
                className="grid grid-cols-[1fr_auto] gap-3 items-center px-3 py-2.5 sm:px-4"
                data-testid={`trash-row-${r._kind}-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono uppercase tracking-wider bg-white/[0.04] text-[#647087] px-1.5 py-0.5 rounded">
                      {KIND_LABELS[r._kind] || r._kind}
                    </span>
                    <span className="text-sm text-white truncate">{labelForRow(r)}</span>
                  </div>
                  <div className="text-[11px] text-[#647087] mt-0.5 flex flex-wrap gap-x-3">
                    <span>Deleted {fmtDate(r.deleted_at)}</span>
                    {r.deleted_by_name && <span>by {r.deleted_by_name}</span>}
                    <span
                      className={`inline-flex items-center gap-1 ${
                        (r.days_until_purge ?? 90) < 7 ? "text-amber-300 font-semibold" : ""
                      }`}
                    >
                      {(r.days_until_purge ?? 90) < 7 && <AlertTriangle className="w-3 h-3" />}
                      Purges in {r.days_until_purge ?? 90}d
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => restore(r)}
                    className="hv-btn hv-btn-primary text-xs"
                    data-testid={`trash-restore-${r._kind}-${r.id}`}
                    title="Restore this record"
                  >
                    <Undo2 className="w-3.5 h-3.5" /> Restore
                  </button>
                  <button
                    type="button"
                    onClick={() => purge(r)}
                    className="hv-btn hv-btn-ghost text-xs text-rose-300 hover:bg-rose-500/10"
                    data-testid={`trash-purge-${r._kind}-${r.id}`}
                    title="Permanently delete (cannot be undone)"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
